import { useRef, useMemo } from 'react';
import { useThree, useLoader, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

export function ImagePlane({
  imageSrc,
  opacity = 1,
  scale = 1,
  positionX = 0
}) {
  const materialRef = useRef();
  const meshRef = useRef();
  const { viewport, size } = useThree();

  const texture = useLoader(THREE.TextureLoader, imageSrc);

  // Update opacity and position every frame
  useFrame(() => {
    if (materialRef.current) {
      materialRef.current.opacity = opacity;
    }
    if (meshRef.current) {
      meshRef.current.position.x = positionX;
    }
  });

  // Calculate dimensions to COVER the viewport completely (no black bars)
  const dimensions = useMemo(() => {
    if (!texture || !texture.image) {
      return { width: viewport.width * 2, height: viewport.height * 2 };
    }

    const imageAspect = texture.image.width / texture.image.height;
    const screenAspect = size.width / size.height;

    let planeWidth, planeHeight;

    // For COVER: we need to ensure BOTH dimensions are >= viewport
    // Scale up the image so that the smaller relative dimension matches viewport
    if (imageAspect > screenAspect) {
      // Image is wider than screen - height is the limiting factor
      // Scale so height fills viewport, width will overflow
      planeHeight = viewport.height;
      planeWidth = planeHeight * imageAspect;
    } else {
      // Image is taller than screen - width is the limiting factor
      // Scale so width fills viewport, height will overflow
      planeWidth = viewport.width;
      planeHeight = planeWidth / imageAspect;
    }

    // Add 20% buffer to guarantee full coverage
    return {
      width: planeWidth * 1.2,
      height: planeHeight * 1.2
    };
  }, [texture, viewport.width, viewport.height, size.width, size.height]);

  if (!texture || !texture.image) return null;

  return (
    <mesh ref={meshRef} position={[positionX, 0, -1]} scale={scale}>
      <planeGeometry args={[dimensions.width, dimensions.height]} />
      <meshBasicMaterial
        ref={materialRef}
        map={texture}
        transparent={true}
        opacity={opacity}
        toneMapped={false}
        depthWrite={false}
      />
    </mesh>
  );
}
